// src/modules/Shop/contexts/CartContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../config/supabase';

const CartContext = createContext({});

// Gerar ID de sessao unico para visitantes
const getSessionId = () => {
  let sessionId = localStorage.getItem('vega_shop_session');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    localStorage.setItem('vega_shop_session', sessionId);
  }
  return sessionId;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cartId, setCartId] = useState(null);

  const sessionId = getSessionId();

  // Calculos
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  // Carregar carrinho ao iniciar
  useEffect(() => {
    loadCart();
  }, []);

  // Salvar carrinho quando mudar
  useEffect(() => {
    if (!loading && cartId) {
      saveCart();
    }
  }, [items]);

  const loadCart = async () => {
    try {
      // Tentar carregar do localStorage primeiro (rapido)
      const localCart = localStorage.getItem('vega_shop_cart');
      if (localCart) {
        const parsed = JSON.parse(localCart);
        setItems(parsed.items || []);
        setCartId(parsed.cartId);
      }

      // Sincronizar com Supabase
      const { data, error } = await supabase
        .from('shop_carts')
        .select('*')
        .eq('session_id', sessionId)
        .single();

      if (data) {
        setItems(data.items || []);
        setCartId(data.id);
        localStorage.setItem('vega_shop_cart', JSON.stringify({
          items: data.items || [],
          cartId: data.id
        }));
      } else if (!error || error.code === 'PGRST116') {
        // Criar novo carrinho
        const { data: newCart } = await supabase
          .from('shop_carts')
          .insert({ session_id: sessionId, items: [] })
          .select()
          .single();

        if (newCart) {
          setCartId(newCart.id);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar carrinho:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveCart = async () => {
    try {
      // Salvar no localStorage
      localStorage.setItem('vega_shop_cart', JSON.stringify({
        items,
        cartId
      }));

      // Salvar no Supabase
      if (cartId) {
        await supabase
          .from('shop_carts')
          .update({
            items,
            subtotal,
            last_activity: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('id', cartId);
      }
    } catch (error) {
      console.error('Erro ao salvar carrinho:', error);
    }
  };

  const addItem = useCallback((product, quantity = 1) => {
    setItems(currentItems => {
      const existingIndex = currentItems.findIndex(item => item.product_id === product.id);

      if (existingIndex >= 0) {
        // Atualizar quantidade
        const updated = [...currentItems];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + quantity
        };
        return updated;
      }

      // Adicionar novo item
      return [...currentItems, {
        product_id: product.id,
        name: product.name,
        slug: product.slug,
        price: product.price,
        image: product.images?.[0] || '',
        quantity,
        sku: product.sku,
        weight_kg: product.weight_kg,
        height_cm: product.height_cm,
        width_cm: product.width_cm,
        length_cm: product.length_cm
      }];
    });

    setIsOpen(true);
  }, []);

  const updateQuantity = useCallback((productId, quantity) => {
    if (quantity < 1) {
      removeItem(productId);
      return;
    }

    setItems(currentItems =>
      currentItems.map(item =>
        item.product_id === productId
          ? { ...item, quantity }
          : item
      )
    );
  }, []);

  const removeItem = useCallback((productId) => {
    setItems(currentItems =>
      currentItems.filter(item => item.product_id !== productId)
    );
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    localStorage.removeItem('vega_shop_cart');
  }, []);

  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);
  const toggleCart = useCallback(() => setIsOpen(prev => !prev), []);

  const value = {
    items,
    itemCount,
    subtotal,
    isOpen,
    loading,
    cartId,
    sessionId,
    addItem,
    updateQuantity,
    removeItem,
    clearCart,
    openCart,
    closeCart,
    toggleCart
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart deve ser usado dentro de CartProvider');
  }
  return context;
};

export default CartContext;
